// src/pages/TrophyCabinet.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrophyStorage } from "../services/trophyStorage";

/**
 * TrophyCabinet — Piano With Pan Trophy Wall
 *
 * V2 Updates:
 * ✅ Restyled to match NewHomeTwo.jsx look and feel
 * ✅ Warmer PWP background, Fredoka/Nunito fonts, rounded cards, playful borders
 * ✅ Easier trophy ladder:
 *    5  → Bronze 1
 *    7  → Silver 1
 *    10 → Gold 1
 *    15 → Bronze 2
 *    17 → Silver 2
 *    20 → Gold 2
 *    ... continues until:
 *    95 → Bronze 10
 *    97 → Silver 10
 *    100 → Diamond Statue
 * ✅ Trophy display is calculated from run number, so old saved runs display with new rules
 * ✅ Diamond statue support added
 */

const STATUE_FALLBACK_IMG = "/images/trophies/statue-mock.png";

const TIER_STATUE_IMG = {
  stone: "/images/trophies/statue-stone.png",
  bronze: "/images/trophies/statue-bronze.png",
  silver: "/images/trophies/statue-silver.png",
  gold: "/images/trophies/statue-gold.png",
  diamond: "/images/trophies/statue-diamond.png",
};

const LAST_SEEN_ROUND_ID_KEY = "pwp_last_seen_trophy_round_id_v1";
const LS_PLAYER_NAME_KEY = "pwp_player_name_v1";

const highlightPulseClass =
  "animate-[pwpPulse_1.2s_ease-in-out_0s_2] ring-4 ring-yellow-200 shadow-xl";

const PASTELS = {
  player: { bg: "#ecfdf5", border: "#86efac", emoji: "🐼" },
  best: { bg: "#f3e8ff", border: "#c4b5fd", emoji: "⚡" },
  streak: { bg: "#fffbeb", border: "#fcd34d", emoji: "🔥" },
  last: { bg: "#eff6ff", border: "#93c5fd", emoji: "🏆" },
};

/* ─────────────────────────────────────────────
   TIME HELPERS
───────────────────────────────────────────── */

function msToHM(ms, { showSeconds = false } = {}) {
  const totalSec = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  if (showSeconds) parts.push(`${String(s).padStart(2, "0")}s`);

  return parts.join(" ");
}

function ymdFromTs(ts) {
  if (!ts) return "—";

  try {
    const d = new Date(ts);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "—";
  }
}

function hmFromTs(ts) {
  if (!ts) return "—";

  try {
    const d = new Date(ts);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return "—";
  }
}

function completedStamp(round) {
  if (round?.meta?.completedYmdHm) return round.meta.completedYmdHm;

  const ymd =
    round?.meta?.completedYmd ||
    round?.meta?.startedYmd ||
    ymdFromTs(round?.completedAt);

  const hm = round?.meta?.completedHm || hmFromTs(round?.completedAt);

  if (ymd && hm && ymd !== "—" && hm !== "—") return `${ymd} • ${hm}`;
  return ymd || "—";
}

/* ─────────────────────────────────────────────
   TROPHY LADDER LOGIC
───────────────────────────────────────────── */

function normalizeTier(tier) {
  if (
    tier === "diamond" ||
    tier === "gold" ||
    tier === "silver" ||
    tier === "bronze" ||
    tier === "stone"
  ) {
    return tier;
  }

  return "stone";
}

function getMilestoneMeta(runNumber) {
  const n = Number(runNumber || 0);

  if (!Number.isFinite(n) || n <= 0) {
    return {
      tier: "stone",
      label: "STATUE",
      shortLabel: "Statue",
      title: "Adventure Statue",
      subtitle: "Complete more runs to upgrade.",
      isMilestone: false,
      level: null,
      emoji: "🗿",
    };
  }

  if (n >= 100) {
    return {
      tier: "diamond",
      label: n === 100 ? "DIAMOND" : "DIAMOND+",
      shortLabel: n === 100 ? "Diamond" : "Diamond+",
      title: "Diamond Statue",
      subtitle: "The legendary 100-run reward.",
      isMilestone: true,
      level: 1,
      emoji: "💎",
    };
  }

  const mod = n % 10;

  if (mod === 5) {
    const level = Math.floor(n / 10) + 1;

    return {
      tier: "bronze",
      label: `BRONZE ${level}`,
      shortLabel: `Bronze ${level}`,
      title: `Bronze Statue ${level}`,
      subtitle: `Unlocked at ${n} full runs.`,
      isMilestone: true,
      level,
      emoji: "🥉",
    };
  }

  if (mod === 7) {
    const level = Math.floor(n / 10) + 1;

    return {
      tier: "silver",
      label: `SILVER ${level}`,
      shortLabel: `Silver ${level}`,
      title: `Silver Statue ${level}`,
      subtitle: `Unlocked at ${n} full runs.`,
      isMilestone: true,
      level,
      emoji: "🥈",
    };
  }

  if (mod === 0) {
    const level = Math.floor(n / 10);

    return {
      tier: "gold",
      label: `GOLD ${level}`,
      shortLabel: `Gold ${level}`,
      title: `Gold Statue ${level}`,
      subtitle: `Unlocked at ${n} full runs.`,
      isMilestone: true,
      level,
      emoji: "🥇",
    };
  }

  return {
    tier: "stone",
    label: "STATUE",
    shortLabel: "Statue",
    title: "Adventure Statue",
    subtitle: "A full adventure run completed.",
    isMilestone: false,
    level: null,
    emoji: "🗿",
  };
}

function getRoundMilestoneMeta(round) {
  return getMilestoneMeta(round?.roundNumber || 0);
}

function buildMilestoneSeries() {
  const series = [];

  for (let base = 0; base <= 90; base += 10) {
    const bronzeRun = base + 5;
    const silverRun = base + 7;
    const goldRun = base + 10;

    if (bronzeRun < 100) {
      series.push({
        run: bronzeRun,
        ...getMilestoneMeta(bronzeRun),
      });
    }

    if (silverRun < 100) {
      series.push({
        run: silverRun,
        ...getMilestoneMeta(silverRun),
      });
    }

    if (goldRun < 100) {
      series.push({
        run: goldRun,
        ...getMilestoneMeta(goldRun),
      });
    } else {
      series.push({
        run: 100,
        ...getMilestoneMeta(100),
      });
    }
  }

  return series;
}

const MILESTONE_SERIES = buildMilestoneSeries();

function getNextMilestone(totalRuns) {
  const t = Math.max(0, Number(totalRuns || 0));
  return MILESTONE_SERIES.find((m) => m.run > t) || null;
}

function getUpcomingMilestones(totalRuns, count = 6) {
  const t = Math.max(0, Number(totalRuns || 0));
  const upcoming = MILESTONE_SERIES.filter((m) => m.run > t).slice(0, count);

  if (upcoming.length > 0) return upcoming;

  return MILESTONE_SERIES.slice(-count);
}

function getTierRingClass(tier) {
  const t = normalizeTier(tier);

  if (t === "diamond") return "ring-4 ring-cyan-200";
  if (t === "gold") return "ring-4 ring-yellow-200";
  if (t === "silver") return "ring-4 ring-slate-200";
  if (t === "bronze") return "ring-4 ring-orange-200";

  return "ring-2 ring-slate-100";
}

function getTierPillClass(tier) {
  const t = normalizeTier(tier);

  if (t === "diamond") {
    return "bg-cyan-50 border-cyan-200 text-cyan-700";
  }

  if (t === "gold") {
    return "bg-yellow-50 border-yellow-200 text-yellow-700";
  }

  if (t === "silver") {
    return "bg-slate-50 border-slate-200 text-slate-700";
  }

  if (t === "bronze") {
    return "bg-orange-50 border-orange-200 text-orange-700";
  }

  return "bg-slate-50 border-slate-200 text-slate-500";
}

/* ─────────────────────────────────────────────
   STORAGE HELPERS
───────────────────────────────────────────── */

function safeReadTrophyState() {
  try {
    if (typeof TrophyStorage?.getState === "function") {
      return TrophyStorage.getState();
    }

    if (typeof TrophyStorage?.getAll === "function") {
      return TrophyStorage.getAll();
    }

    if (typeof TrophyStorage?.read === "function") {
      return TrophyStorage.read();
    }
  } catch (e) {
    console.warn("Could not read trophy state:", e);
  }

  return null;
}

function getLastSeenRoundId() {
  try {
    return window?.localStorage?.getItem(LAST_SEEN_ROUND_ID_KEY) || null;
  } catch {
    return null;
  }
}

function setLastSeenRoundId(id) {
  try {
    window?.localStorage?.setItem(LAST_SEEN_ROUND_ID_KEY, String(id || ""));
  } catch {}
}

function readLocalPlayerName() {
  try {
    const v = window?.localStorage?.getItem(LS_PLAYER_NAME_KEY);
    const clean = (v || "").trim();
    return clean ? clean : "";
  } catch {
    return "";
  }
}

function writeLocalPlayerName(name) {
  const clean = (name || "").toString().trim().slice(0, 40);

  if (!clean) return false;

  try {
    window?.localStorage?.setItem(LS_PLAYER_NAME_KEY, clean);
    return true;
  } catch {
    return false;
  }
}

/* ─────────────────────────────────────────────
   UI COMPONENTS
───────────────────────────────────────────── */

function PlayerNameModal({ open, currentName, onClose, onSave }) {
  const [name, setName] = useState(currentName || "");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setName(currentName || "");
      setErr("");
    }
  }, [open, currentName]);

  if (!open) return null;

  const save = () => {
    const ok = writeLocalPlayerName(name);

    if (!ok) {
      setErr("Please type a valid name.");
      return;
    }

    onSave?.(readLocalPlayerName());
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 px-3">
      <div className="pwp-modal-card w-full max-w-lg">
        <div className="p-5 sm:p-6">
          <div className="pwp-title text-2xl text-slate-900">
            Enter your name
          </div>

          <div className="mt-1 text-sm text-slate-600 font-extrabold">
            This will appear on your certificate and trophy wall.
          </div>

          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Aarav / Meera"
            maxLength={40}
            className="mt-4 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 text-base font-extrabold text-slate-900 outline-none focus:ring-4 focus:ring-purple-100 focus:border-purple-300"
          />

          {err ? (
            <div className="mt-2 text-sm font-extrabold text-rose-700">
              {err}
            </div>
          ) : null}

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="pwp-small-btn pwp-small-btn-light"
            >
              Not now
            </button>

            <button onClick={save} className="pwp-small-btn pwp-small-btn-dark">
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatueThumb({ tier, size = "sm", dimmed = false }) {
  const t = normalizeTier(tier);
  const preferredSrc = TIER_STATUE_IMG[t] || STATUE_FALLBACK_IMG;

  const [src, setSrc] = useState(preferredSrc);

  useEffect(() => {
    setSrc(preferredSrc);
  }, [preferredSrc]);

  const sizing =
    size === "xl"
      ? "h-[38vh] sm:h-[46vh] max-h-[500px]"
      : size === "lg"
      ? "h-40 sm:h-52"
      : size === "sm2"
      ? "h-16 sm:h-20"
      : "h-20 sm:h-24";

  return (
    <div className="flex items-center justify-center">
      <img
        src={src}
        alt=""
        className={`${sizing} object-contain drop-shadow-xl ${
          dimmed ? "opacity-50 grayscale" : ""
        }`}
        onError={() => {
          if (src !== STATUE_FALLBACK_IMG) {
            setSrc(STATUE_FALLBACK_IMG);
          }
        }}
      />
    </div>
  );
}

function MilestoneTile({ milestone, totalRuns }) {
  const unlocked = totalRuns >= milestone.run;
  const pillClass = getTierPillClass(milestone.tier);

  return (
    <div
      className={`pwp-milestone-tile ${
        unlocked ? "pwp-milestone-unlocked" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className={`pwp-tier-pill ${pillClass}`}>{milestone.label}</div>
        <div className="text-xs font-black text-slate-500">
          Run {milestone.run}
        </div>
      </div>

      <div className="mt-3">
        <StatueThumb tier={milestone.tier} size="sm2" dimmed={!unlocked} />
      </div>

      <div className="mt-3 text-sm font-black text-slate-900">
        {milestone.title}
      </div>

      <div className="mt-1 text-xs font-extrabold text-slate-500">
        {unlocked
          ? "Unlocked!"
          : `${milestone.run - totalRuns} more run${
              milestone.run - totalRuns === 1 ? "" : "s"
            }`}
      </div>
    </div>
  );
}

function TileModal({ open, round, playerName, onClose }) {
  if (!open || !round) return null;

  const meta = getRoundMilestoneMeta(round);
  const completedLabel = completedStamp(round);

  const levelsCompleted = round?.meta?.levelsCompleted || {};
  const starsCollected = round?.meta?.starsCollected || {};

  const levelsDone = Object.values(levelsCompleted).filter(Boolean).length;
  const starsDone = Object.values(starsCollected).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/55 px-3">
      <div className="pwp-modal-card w-full max-w-lg">
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className={`pwp-tier-pill ${getTierPillClass(meta.tier)}`}>
                {meta.emoji} {meta.label} • RUN #{round.roundNumber}
              </div>

              <div className="mt-3 pwp-title text-2xl text-slate-900">
                {playerName || "Player"}
              </div>

              <div className="mt-1 text-sm text-slate-600 font-extrabold">
                Completed on <span className="font-black">{completedLabel}</span>
                {" "}• Time{" "}
                <span className="font-black">{msToHM(round.durationMs)}</span>
              </div>
            </div>

            <button onClick={onClose} className="pwp-small-btn pwp-small-btn-light">
              Close
            </button>
          </div>

          <div className="mt-5 flex items-center justify-center rounded-3xl bg-gradient-to-b from-white to-yellow-50 border-2 border-yellow-100 p-4">
            <StatueThumb tier={meta.tier} size="lg" dimmed={false} />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="pwp-mini-stat">
              <div className="pwp-mini-label">Levels completed</div>
              <div className="pwp-mini-number">{levelsDone}</div>
            </div>

            <div className="pwp-mini-stat">
              <div className="pwp-mini-label">Stars collected</div>
              <div className="pwp-mini-number">{starsDone}</div>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border-2 border-slate-100 bg-white p-4">
            <div className="pwp-mini-label">Run notes</div>

            <div className="mt-2 text-sm text-slate-700 font-bold leading-6">
              Final Boss:{" "}
              <span className="font-black">{round?.meta?.finalBoss || "—"}</span>
              <br />
              Song: <span className="font-black">{round?.meta?.song || "—"}</span>
              <br />
              Level:{" "}
              <span className="font-black">{round?.meta?.levelId ?? "—"}</span>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border-2 border-purple-100 bg-purple-50 p-4 text-sm text-purple-900">
            <div className="font-black">New reward ladder</div>

            <div className="mt-1 font-extrabold">
              5 → Bronze • 7 → Silver • 10 → Gold. Then the same pattern repeats
              every 10 runs until the Diamond Statue at 100.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewStatueModal({ open, round, playerName, onClose }) {
  if (!open || !round) return null;

  const meta = getRoundMilestoneMeta(round);
  const completedLabel = completedStamp(round);
  const timeTaken = msToHM(round.durationMs);

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-900/65 px-3">
      <div className="pwp-modal-card w-full max-w-3xl">
        <div className="p-5 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className={`pwp-tier-pill ${getTierPillClass(meta.tier)}`}>
                {meta.emoji} New Statue Collected • {meta.label}
              </div>

              <div className="mt-3 pwp-title text-3xl sm:text-4xl text-slate-900">
                {playerName || "Player"}, you did it! 🎉
              </div>

              <div className="mt-2 text-sm sm:text-base text-slate-600 font-extrabold">
                Completed on <span className="font-black">{completedLabel}</span>
                {" "}• Time <span className="font-black">{timeTaken}</span>
              </div>
            </div>

            <button onClick={onClose} className="pwp-small-btn pwp-small-btn-light">
              Awesome!
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center rounded-[28px] bg-gradient-to-b from-white via-yellow-50 to-purple-50 border-2 border-yellow-100 p-4">
            <StatueThumb tier={meta.tier} size="xl" />
          </div>

          <div className="mt-6 rounded-3xl border-2 border-green-100 bg-green-50 p-4 text-green-900">
            <div className="font-black text-sm">What happens now?</div>

            <div className="mt-1 text-sm font-extrabold">
              This statue is now saved in your Trophy Wall. Keep replaying the
              full adventure to unlock Bronze, Silver, Gold and finally the
              Diamond Statue at 100 runs.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */

export default function TrophyCabinet() {
  const navigate = useNavigate();

  const [storageState, setStorageState] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);

  const [newStatueRound, setNewStatueRound] = useState(null);
  const [openNewStatue, setOpenNewStatue] = useState(false);

  const tileRefs = useRef(new Map());
  const [highlightId, setHighlightId] = useState(null);

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [localPlayerName, setLocalPlayerName] = useState("");

  useEffect(() => {
    const s = safeReadTrophyState();
    setStorageState(s);
    setLocalPlayerName(readLocalPlayerName());
  }, []);

  const playerName = (localPlayerName || storageState?.playerName || "").trim();

  const rounds = useMemo(() => {
    const arr = Array.isArray(storageState?.rounds) ? storageState.rounds : [];

    return [...arr].sort((a, b) => {
      return (b?.completedAt || 0) - (a?.completedAt || 0);
    });
  }, [storageState]);

  const totalRuns = rounds.length;

  const headerStats = useMemo(() => {
    const personalBestMs = storageState?.personalBestMs ?? null;
    const currentStreakDays = storageState?.currentStreakDays ?? 0;

    const lastCompletionYmd =
      storageState?.lastCompletionYmd ||
      (storageState?.lastCompletionAt
        ? ymdFromTs(storageState.lastCompletionAt)
        : null);

    return {
      personalBestMs,
      currentStreakDays,
      lastCompletionYmd,
    };
  }, [storageState]);

  const nextMilestone = useMemo(() => {
    return getNextMilestone(totalRuns);
  }, [totalRuns]);

  const upcomingMilestones = useMemo(() => {
    return getUpcomingMilestones(totalRuns, 6);
  }, [totalRuns]);

  const diamondProgress = Math.min(100, Math.round((totalRuns / 100) * 100));

  useEffect(() => {
    if (!rounds.length) return;

    const newest = rounds[0];

    if (!newest?.id) return;

    const lastSeen = getLastSeenRoundId();

    if (lastSeen !== newest.id) {
      setNewStatueRound(newest);
      setOpenNewStatue(true);
    }
  }, [rounds]);

  function closeNewStatueModal() {
    const r = newStatueRound;

    if (r?.id) setLastSeenRoundId(r.id);

    setOpenNewStatue(false);

    setTimeout(() => {
      if (!r?.id) return;

      const el = tileRefs.current.get(r.id);

      if (el?.scrollIntoView) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      setHighlightId(r.id);
      setTimeout(() => setHighlightId(null), 2600);
    }, 250);
  }

  const featuredMode = totalRuns === 1;

  return (
    <div className="pwp-trophy-root">
      <style>{TROPHY_CSS}</style>

      <PlayerNameModal
        open={nameModalOpen}
        currentName={playerName}
        onClose={() => setNameModalOpen(false)}
        onSave={(newName) => setLocalPlayerName((newName || "").trim())}
      />

      <NewStatueModal
        open={openNewStatue}
        round={newStatueRound}
        playerName={playerName}
        onClose={closeNewStatueModal}
      />

      <TileModal
        open={!!selectedRound}
        round={selectedRound}
        playerName={playerName}
        onClose={() => setSelectedRound(null)}
      />

      <main className="pwp-trophy-main">
        <section className="pwp-trophy-hero">
          <div>
            <div className="pwp-eyebrow">Piano With Pan Rewards</div>

            <h1 className="pwp-title pwp-page-title">Trophy Cabinet</h1>

            <p className="pwp-page-sub">
              Every full adventure run saves a new statue. Keep replaying to
              upgrade your trophy wall and reach the Diamond Statue.
            </p>
          </div>

          <div className="pwp-action-row">
            <button
              onClick={() => setNameModalOpen(true)}
              className="pwp-small-btn pwp-small-btn-light"
              title="Set your name for certificate + trophies"
            >
              {playerName ? "✏️ Edit Name" : "✍️ Add Name"}
            </button>

            <button
              onClick={() => navigate("/level-1")}
              className="pwp-play-btn"
              title="Play again from Level 1"
            >
              ▶️ Play Again
            </button>
          </div>
        </section>

        <section className="pwp-progress-card">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div>
              <div className="pwp-tier-pill bg-yellow-50 border-yellow-200 text-yellow-700">
                🏆 New Trophy Ladder
              </div>

              <h2 className="pwp-title text-2xl sm:text-3xl mt-3 text-slate-900">
                Easier trophies. More magic.
              </h2>

              <p className="mt-2 text-sm sm:text-base text-slate-600 font-extrabold max-w-2xl">
                Bronze at 5, Silver at 7, Gold at 10 — then the ladder repeats
                every 10 runs until the legendary Diamond Statue at 100.
              </p>
            </div>

            <div className="pwp-next-box">
              <div className="text-xs font-black uppercase tracking-wider text-slate-500">
                Next unlock
              </div>

              {nextMilestone ? (
                <>
                  <div className="pwp-title text-xl text-slate-900 mt-1">
                    {nextMilestone.emoji} {nextMilestone.shortLabel}
                  </div>

                  <div className="text-sm font-extrabold text-slate-600 mt-1">
                    {nextMilestone.run - totalRuns} more run
                    {nextMilestone.run - totalRuns === 1 ? "" : "s"}
                  </div>
                </>
              ) : (
                <>
                  <div className="pwp-title text-xl text-cyan-700 mt-1">
                    💎 Diamond reached!
                  </div>

                  <div className="text-sm font-extrabold text-slate-600 mt-1">
                    You are officially a legend.
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between text-xs font-black text-slate-500 mb-2">
              <span>{totalRuns}/100 runs</span>
              <span>{diamondProgress}% to Diamond</span>
            </div>

            <div className="pwp-diamond-track">
              <div
                className="pwp-diamond-fill"
                style={{ width: `${diamondProgress}%` }}
              />
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div
            className="pwp-stat-card"
            style={{
              background: PASTELS.player.bg,
              borderColor: PASTELS.player.border,
            }}
          >
            <div className="pwp-stat-top">
              <span>{PASTELS.player.emoji}</span>
              <span>Player</span>
            </div>

            <div className="pwp-stat-value">{playerName || "—"}</div>

            {!playerName ? (
              <button
                onClick={() => setNameModalOpen(true)}
                className="mt-2 text-xs font-black text-green-700 underline"
              >
                Add name
              </button>
            ) : null}
          </div>

          <div
            className="pwp-stat-card"
            style={{
              background: PASTELS.best.bg,
              borderColor: PASTELS.best.border,
            }}
          >
            <div className="pwp-stat-top">
              <span>{PASTELS.best.emoji}</span>
              <span>Personal Best</span>
            </div>

            <div className="pwp-stat-value">
              {headerStats.personalBestMs
                ? msToHM(headerStats.personalBestMs)
                : "—"}
            </div>
          </div>

          <div
            className="pwp-stat-card"
            style={{
              background: PASTELS.streak.bg,
              borderColor: PASTELS.streak.border,
            }}
          >
            <div className="pwp-stat-top">
              <span>{PASTELS.streak.emoji}</span>
              <span>Current Streak</span>
            </div>

            <div className="pwp-stat-value">
              {headerStats.currentStreakDays} days
            </div>
          </div>

          <div
            className="pwp-stat-card"
            style={{
              background: PASTELS.last.bg,
              borderColor: PASTELS.last.border,
            }}
          >
            <div className="pwp-stat-top">
              <span>{PASTELS.last.emoji}</span>
              <span>Last Completion</span>
            </div>

            <div className="pwp-stat-value">
              {headerStats.lastCompletionYmd || "—"}
            </div>
          </div>
        </section>

        <section className="pwp-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="pwp-title text-xl text-slate-900">
                Trophy Wall
              </h2>

              <p className="text-xs sm:text-sm text-slate-500 font-extrabold mt-1">
                Tap a statue tile to see details.
              </p>
            </div>

            <div className="pwp-tier-pill bg-green-50 border-green-200 text-green-700">
              {totalRuns} run{totalRuns === 1 ? "" : "s"}
            </div>
          </div>

          {totalRuns === 0 ? (
            <div className="mt-6 pwp-empty-box">
              <div className="text-4xl">🐉</div>

              <div className="mt-3 pwp-title text-xl text-slate-900">
                No trophies yet
              </div>

              <p className="mt-1 text-sm text-slate-600 font-extrabold">
                Defeat Doron and collect your first adventure statue.
              </p>

              <button
                onClick={() => navigate("/level-1")}
                className="mt-4 pwp-play-btn"
              >
                Start Level 1
              </button>
            </div>
          ) : (
            <>
              {featuredMode ? (
                <div className="mt-5">
                  {rounds.map((r) => {
                    const meta = getRoundMilestoneMeta(r);
                    const completedLabel = completedStamp(r);
                    const timeTaken = msToHM(r.durationMs);

                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setSelectedRound(r)}
                        ref={(el) => {
                          if (el) tileRefs.current.set(r.id, el);
                        }}
                        className={
                          "pwp-featured-trophy " +
                          getTierRingClass(meta.tier) +
                          (highlightId === r.id
                            ? ` ${highlightPulseClass}`
                            : "")
                        }
                        title="View trophy details"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-xs sm:text-sm font-black text-slate-500 uppercase tracking-wider">
                            Run #{r.roundNumber}
                          </div>

                          <div
                            className={`pwp-tier-pill ${getTierPillClass(
                              meta.tier
                            )}`}
                          >
                            {meta.emoji} {meta.label}
                          </div>
                        </div>

                        <div className="mt-6 flex items-center justify-center">
                          <StatueThumb tier={meta.tier} size="xl" />
                        </div>

                        <div className="mt-5">
                          <div className="pwp-title text-xl sm:text-2xl text-slate-900">
                            {playerName || "Player"}
                          </div>

                          <div className="text-sm text-slate-600 font-extrabold mt-1">
                            {completedLabel} • {timeTaken}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                  {rounds.map((r) => {
                    const meta = getRoundMilestoneMeta(r);
                    const completedLabel = completedStamp(r);
                    const timeTaken = msToHM(r.durationMs);

                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setSelectedRound(r)}
                        ref={(el) => {
                          if (el) tileRefs.current.set(r.id, el);
                        }}
                        className={
                          "pwp-trophy-tile " +
                          getTierRingClass(meta.tier) +
                          (highlightId === r.id
                            ? ` ${highlightPulseClass}`
                            : "")
                        }
                        title="View trophy details"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                            Run #{r.roundNumber}
                          </div>

                          <div
                            className={`pwp-mini-pill ${getTierPillClass(
                              meta.tier
                            )}`}
                          >
                            {meta.label}
                          </div>
                        </div>

                        <div className="mt-3">
                          <StatueThumb tier={meta.tier} />
                        </div>

                        <div className="mt-3">
                          <div className="text-xs font-black text-slate-900 truncate">
                            {playerName || "Player"}
                          </div>

                          <div className="text-[11px] text-slate-500 font-extrabold mt-0.5 leading-snug">
                            {completedLabel}
                            <br />
                            {timeTaken}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>

        <section className="pwp-card">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h2 className="pwp-title text-xl text-slate-900">
                Next Trophy Unlocks
              </h2>

              <p className="text-xs sm:text-sm text-slate-500 font-extrabold mt-1">
                A faster reward ladder keeps kids motivated to replay and improve.
              </p>
            </div>

            <div className="pwp-tier-pill bg-purple-50 border-purple-200 text-purple-700">
              💎 Diamond at 100
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {upcomingMilestones.map((m) => (
              <MilestoneTile
                key={`${m.run}-${m.label}`}
                milestone={m}
                totalRuns={totalRuns}
              />
            ))}
          </div>

          <div className="mt-5 rounded-3xl border-2 border-slate-100 bg-slate-50 p-4 text-sm text-slate-600">
            <div className="font-black text-slate-800">How it works</div>

            <div className="mt-1 font-extrabold">
              5 → Bronze • 7 → Silver • 10 → Gold. Then it repeats:
              15 → Bronze 2, 17 → Silver 2, 20 → Gold 2, all the way to
              the Diamond Statue at 100.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLES — matched with NewHomeTwo.jsx look and feel
═══════════════════════════════════════════════════════════ */

const TROPHY_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');

.pwp-trophy-root {
  min-height: 100vh;
  font-family: 'Nunito', sans-serif;
  color: #0f172a;
  background:
    radial-gradient(circle at 10% 10%, rgba(251, 191, 36, 0.22), transparent 28%),
    radial-gradient(circle at 90% 20%, rgba(124, 58, 237, 0.12), transparent 30%),
    linear-gradient(180deg, #fffbeb 0%, #ffffff 48%, #f5f3ff 100%);
}

.pwp-trophy-root * {
  box-sizing: border-box;
}

.pwp-trophy-main {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 28px 16px 80px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.pwp-title {
  font-family: 'Fredoka One', cursive;
  letter-spacing: 0.01em;
}

.pwp-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fef3c7;
  border: 2px solid #fcd34d;
  color: #92400e;
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.pwp-page-title {
  margin-top: 12px;
  font-size: clamp(2rem, 7vw, 3.6rem);
  line-height: 1.02;
  color: #0f172a;
}

.pwp-page-sub {
  margin-top: 10px;
  max-width: 680px;
  color: #64748b;
  font-weight: 900;
  font-size: 1rem;
  line-height: 1.55;
}

.pwp-trophy-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.pwp-action-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.pwp-card,
.pwp-progress-card {
  background: rgba(255,255,255,0.92);
  border: 3px solid #e2e8f0;
  border-radius: 30px;
  box-shadow: 0 8px 0 rgba(15, 23, 42, 0.08), 0 18px 38px rgba(15, 23, 42, 0.08);
  padding: 18px;
}

.pwp-progress-card {
  border-color: #fcd34d;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.94), rgba(255,251,235,0.92));
}

.pwp-next-box {
  min-width: 230px;
  background: #ffffff;
  border: 3px solid #ede9fe;
  border-radius: 24px;
  padding: 18px;
  box-shadow: 0 5px 0 #c4b5fd;
}

.pwp-diamond-track {
  height: 15px;
  width: 100%;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
  border: 2px solid #dbeafe;
}

.pwp-diamond-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #22c55e, #fbbf24, #06b6d4);
  transition: width 0.35s ease;
}

.pwp-stat-card {
  border: 3px solid;
  border-radius: 24px;
  padding: 16px;
  min-height: 112px;
  box-shadow: 0 5px 0 rgba(15, 23, 42, 0.08);
}

.pwp-stat-top {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.pwp-stat-value {
  margin-top: 8px;
  color: #0f172a;
  font-size: 1.2rem;
  font-weight: 1000;
  line-height: 1.1;
}

.pwp-play-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(180deg, #22c55e, #16a34a);
  color: #fff;
  border: 3px solid #0f172a;
  border-radius: 999px;
  padding: 11px 20px;
  font-family: 'Fredoka One', cursive;
  font-size: 0.95rem;
  box-shadow: 0 5px 0 #14532d, 0 10px 24px rgba(34,197,94,0.22);
  transition: transform 0.15s, box-shadow 0.15s;
}

.pwp-play-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 7px 0 #14532d, 0 14px 28px rgba(34,197,94,0.28);
}

.pwp-play-btn:active {
  transform: translateY(2px);
  box-shadow: 0 2px 0 #14532d;
}

.pwp-small-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  padding: 9px 15px;
  font-weight: 1000;
  font-size: 0.85rem;
  transition: all 0.15s;
}

.pwp-small-btn-light {
  background: #ffffff;
  color: #0f172a;
  border: 2px solid #e2e8f0;
}

.pwp-small-btn-light:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.pwp-small-btn-dark {
  background: #0f172a;
  color: #ffffff;
  border: 2px solid #0f172a;
}

.pwp-small-btn-dark:hover {
  background: #1e293b;
}

.pwp-tier-pill,
.pwp-mini-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 2px solid;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.72rem;
  font-weight: 1000;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.pwp-mini-pill {
  padding: 4px 7px;
  font-size: 0.58rem;
}

.pwp-featured-trophy,
.pwp-trophy-tile {
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: 3px solid #e2e8f0;
  border-radius: 26px;
  padding: 16px;
  box-shadow: 0 5px 0 rgba(15, 23, 42, 0.08);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.pwp-featured-trophy {
  padding: 24px;
  border-radius: 32px;
}

.pwp-featured-trophy:hover,
.pwp-trophy-tile:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 0 rgba(15, 23, 42, 0.1), 0 18px 36px rgba(15, 23, 42, 0.09);
  border-color: #c4b5fd;
}

.pwp-empty-box {
  margin-top: 22px;
  text-align: center;
  border: 3px dashed #cbd5e1;
  border-radius: 28px;
  padding: 32px 18px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
}

.pwp-milestone-tile {
  position: relative;
  background: #ffffff;
  border: 3px solid #e2e8f0;
  border-radius: 24px;
  padding: 12px;
  min-height: 190px;
  box-shadow: 0 5px 0 rgba(15, 23, 42, 0.06);
}

.pwp-milestone-tile:not(.pwp-milestone-unlocked) {
  background: linear-gradient(180deg, #ffffff, #f8fafc);
}

.pwp-milestone-unlocked {
  border-color: #86efac;
  background: linear-gradient(180deg, #ffffff, #ecfdf5);
}

.pwp-mini-stat {
  border: 2px solid #e2e8f0;
  border-radius: 20px;
  background: #f8fafc;
  padding: 14px;
}

.pwp-mini-label {
  font-size: 0.68rem;
  font-weight: 1000;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.pwp-mini-number {
  margin-top: 4px;
  font-size: 1.6rem;
  font-weight: 1000;
  color: #0f172a;
}

.pwp-modal-card {
  background: #ffffff;
  border: 3px solid #e2e8f0;
  border-radius: 30px;
  box-shadow: 0 12px 0 rgba(15, 23, 42, 0.14), 0 24px 60px rgba(15, 23, 42, 0.24);
  overflow: hidden;
}

@keyframes pwpPulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.03);
  }

  100% {
    transform: scale(1);
  }
}

@media (max-width: 760px) {
  .pwp-trophy-main {
    padding: 20px 12px 64px;
    gap: 16px;
  }

  .pwp-trophy-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .pwp-action-row {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .pwp-small-btn,
  .pwp-play-btn {
    width: 100%;
  }

  .pwp-card,
  .pwp-progress-card {
    border-radius: 24px;
    padding: 14px;
  }

  .pwp-next-box {
    width: 100%;
    min-width: 0;
  }

  .pwp-page-sub {
    font-size: 0.95rem;
  }

  .pwp-stat-card {
    min-height: 104px;
    padding: 13px;
  }

  .pwp-stat-value {
    font-size: 1rem;
  }

  .pwp-mini-pill {
    font-size: 0.52rem;
    padding: 4px 6px;
  }

  .pwp-trophy-tile {
    padding: 11px;
    border-radius: 22px;
  }

  .pwp-milestone-tile {
    min-height: 180px;
  }
}
`;